import React, { useEffect, useState } from 'react';

import api from '../utils/Api';
import { formatPrice } from './BuyBumps';

import './VenuePayouts.scss';

export type PayoutState = 'none' | 'incomplete' | 'pending' | 'ready';

export interface PayoutStatus {
  // False when the server has no Stripe keys; the Earnings tab is left off then.
  available: boolean;
  state?: PayoutState;
  sharePercent?: number;
}

interface PlayerEarnings {
  total: number;
  thisMonth: number;
  count: number;
  recent: { createdAt: string; productName: string; amount: number; venueShare: number; currency: string }[];
}

interface VenuePayoutsProps {
  token: string;
  // The player this control panel is for. Its earnings are the owner's alone.
  playerId: number;
  status: PayoutStatus;
  busy: boolean;
  onSetup(): void;
  onDashboard(): void;
}

const money = (cents: number, currency = 'usd') => formatPrice(cents, currency);

const saleDate = (iso: string) => {
  const date = new Date(iso);
  return isNaN(date.getTime()) ? '' : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/**
 * The owner's Earnings tab: what this jukebox has earned them, and where they are with
 * Stripe. Signing up and payouts to the bank happen on Stripe's own pages; the per-jukebox
 * breakdown is kept by Jookable, because Stripe only knows the owner's account as a whole.
 */
const VenuePayouts = ({ token, playerId, status, busy, onSetup, onDashboard }: VenuePayoutsProps) => {
  const share = `${status.sharePercent || 30}%`;
  const state = status.state || 'none';
  const [earnings, setEarnings] = useState<PlayerEarnings>(null);
  const [earningsError, setEarningsError] = useState<string>(null);

  useEffect(() => {
    api
      .users(`payouts/player/${playerId}`, 'GET', token, null)
      .then(response => {
        setEarnings(response);
        setEarningsError(null);
      })
      .catch(error => setEarningsError(error.message));
  }, [playerId, token]);

  let text: string;
  let action: { label: string; onClick(): void };
  if (state === 'ready') {
    text = `You get ${share} of every bump pack bought on your jukeboxes. Stripe sends it to your bank once a month.`;
    action = { label: 'See payouts', onClick: onDashboard };
  } else if (state === 'pending') {
    text = 'Stripe is checking your details. Sales start earning for you as soon as it is done.';
    action = { label: 'Open Stripe', onClick: onDashboard };
  } else if (state === 'incomplete') {
    text = 'Stripe still needs a few details before it can pay you. Until then, sales here go to Jookable.';
    action = { label: 'Finish setup', onClick: onSetup };
  } else {
    text = `Get ${share} of every bump pack bought on your jukeboxes. Stripe handles sign-up and sends the money to your bank once a month. Until you set it up, sales here go to Jookable.`;
    action = { label: 'Set up payouts', onClick: onSetup };
  }

  return (
    <div className="control-rules">
      <div className="control-rule">
        <div className="control-rule-label">Earned on this jukebox</div>
        {!earnings && !earningsError && <div className="control-rule-help">Loading…</div>}
        {!!earningsError && <div className="control-rule-help">{earningsError}</div>}
        {!!earnings && (
          <>
            <div className="payouts-figures">
              <div className="payouts-figure">
                <span className="payouts-figure-value">{money(earnings.total)}</span>
                <span className="payouts-figure-label">All time</span>
              </div>
              <div className="payouts-figure">
                <span className="payouts-figure-value">{money(earnings.thisMonth)}</span>
                <span className="payouts-figure-label">This month</span>
              </div>
              <div className="payouts-figure">
                <span className="payouts-figure-value">{earnings.count}</span>
                <span className="payouts-figure-label">{earnings.count === 1 ? 'Pack sold' : 'Packs sold'}</span>
              </div>
            </div>
            {earnings.recent.length === 0 ? (
              <div className="control-rule-help">
                {state === 'ready'
                  ? 'Nothing yet. Packs bought on this jukebox will show here.'
                  : 'Nothing yet. Packs bought here count once you have set up payouts.'}
              </div>
            ) : (
              <ul className="payouts-sales">
                {earnings.recent.map((sale, index) => (
                  <li key={index} className="payouts-sale">
                    <span className="payouts-sale-date">{saleDate(sale.createdAt)}</span>
                    <span className="payouts-sale-name">{sale.productName}</span>
                    <span className="payouts-sale-share">+{money(sale.venueShare, sale.currency)}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      <div className="control-rule">
        <div className="control-rule-label">{state === 'ready' ? 'Payouts are on' : 'Earn from bumps'}</div>
        <div className="control-rule-help">{text}</div>
        <button type="button" className="control-payouts-button" disabled={busy} onClick={action.onClick}>
          {busy ? 'Opening' : action.label}
        </button>
      </div>
      <p className="control-hint">
        Counts packs bought on the web on this jukebox. Packs you buy yourself, and ones bought in the phone app, are not
        included.
      </p>
    </div>
  );
};

export default VenuePayouts;
